import { useMemo } from "react";
import { InventoryRow, WareCatalogItem } from "types";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { getSeller } from "@/utils/getSeller";

interface UseInventoryLotsResult {
    data: InventoryRow[];
    isLoading: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    refetch: () => void;
}

interface ProductEntry {
    id: string;
    name: string;
    photoId?: string;
}

export const useInventoryLots = (
    products: ProductEntry[],
    allMerchandises: ProductEntry[] = [],
    dateRange?: string[],
    skuParam?: Record<string, string>,
): UseInventoryLotsResult => {
    const seller = getSeller();

    const source = products.length > 0 ? products : allMerchandises;
    const ids = source.map(({ id }) => id);

    const queryParams = {
        "product[in]": ids.join(","),
        object: "ware_catalog_item",
        expand: "inventory.inventoryLots",
        seller: seller.id,
        ...(dateRange?.length === 2 && { "createdAt[range]": `${dateRange[0]},${dateRange[1]}` }),
        ...skuParam,
    };

    const {
        data: rawItems = [],
        isLoading,
        hasMore,
        fetchNextPage,
        isFetchingNextPage,
        refetch,
    } = useInfiniteCollection<WareCatalogItem>("catalogItems", queryParams, { enabled: ids.length > 0, staleTime: 0 });

    const productInfoMap = useMemo(() => {
        return Object.fromEntries(source.map(({ id, name, photoId }) => [id, { name, photoId }]));
    }, [source]);

    const rows = useMemo<InventoryRow[]>(
        () =>
            rawItems.map((item) => {
                const lots = item.inventory?.inventoryLots ?? [];
                // Current adjustment UI is variant-level; for multi-lot variants we target a primary lot id.
                const primaryLotId = lots[0]?.id;

                const unavailable = lots.reduce((sum, lot) => sum + (lot.unavailable ?? 0), 0);
                const committed = lots.reduce((sum, lot) => sum + (lot.pendingFulfillment ?? 0), 0);
                const available = lots.reduce((sum, lot) => sum + (lot.available ?? 0), 0);
                const productInfo = productInfoMap[item.product?.id ?? ""];

                return {
                    id: primaryLotId ?? item.id,
                    catalogItemId: item.id,
                    productId: item.product?.id ?? "",
                    productName: productInfo?.name ?? "",
                    variantLabel: item.name,
                    sku: item.sku,
                    thumbnailUrl: item.medias?.[0]?.src,
                    productPhotoId: productInfo?.photoId,
                    isDeleted: !!item.deletedAt,
                    isDraft: item.status === "draft",
                    unavailable,
                    committed,
                    available,
                    onHand: committed + available,
                };
            }),
        [rawItems, productInfoMap],
    );

    return { data: rows, isLoading, hasNextPage: hasMore, fetchNextPage, isFetchingNextPage, refetch };
};
